/**
 * Разбор прошлого периода (docs/ANALYTICS_PERIOD.md §2.5) — числа.
 *
 * Здесь только расчёт: итог периода, наблюдения по свободным тратам против
 * обычного и итоги договорённостей. Слова — у интерфейса (шаблон) и у модели
 * (формулировки, числа в которых проверяются). Совет даём только там, где
 * есть с чем сравнить: без сопоставимой истории «обычно» не бывает.
 */
import { ZERO, add, sub, type Money } from './money.js';
import { diffDays, type PlainDate } from './date.js';
import { periodFacts, type AnalyticsTx } from './analytics.js';
import type { ReportPeriod } from './period.js';
import type { Goal } from './types.js';
import { USUAL_MONTHS, middle } from './usual.js';

/** Договорённость «попробую оставить на кафе около 10 000 ₽». */
export interface PeriodDeal {
  categoryId: string;
  /** Период, по итогам которого договорились. Проверяется следующий. */
  periodKey: string;
  usual: Money;
  target: Money;
  /** Когда договорились. */
  at: string;
  /** «Не про меня» по уже действующей договорённости — она снята. */
  droppedAt?: string;
}

/** Тема, которую человек попросил больше не поднимать («это моё решение»). */
export interface PeriodTopicOff {
  categoryId: string;
  reason: 'important' | 'work';
  at: string;
}

export interface ReviewCategory {
  id: string;
  name: string;
  parentId: string | null;
  defaultPart: 'need' | 'want' | 'save' | null;
  kind?: 'expense' | 'income' | undefined;
  archived?: boolean | undefined;
}

export interface Observation {
  categoryId: string;
  name: string;
  /** Потрачено за период. */
  amount: Money;
  /** Покупок. */
  count: number;
  /** Обычно за сопоставимый период. */
  usual: Money;
  /** Предлагаемая сумма: «оставить около …». */
  target: Money;
  /** На столько меньше прошлого периода, если уложиться. */
  less: Money;
  /** Цель, которую это приблизит, и примерно на сколько месяцев. */
  goal?: { id: string; name: string; months: number };
}

export interface DealResult {
  deal: PeriodDeal;
  name: string;
  /** Потрачено в проверяемом периоде. */
  spent: Money;
  /** Уложились в договорённую сумму. */
  kept: boolean;
  /** На столько меньше, чем в периоде договорённости (может быть меньше нуля). */
  less: Money;
  /** Подряд периодов, где уложились: на третьем договорённость отпускается. */
  streak: number;
}

export interface PeriodReview {
  period: ReportPeriod;
  income: Money;
  spent: Money;
  saved: Money;
  observations: Observation[];
  deals: DealResult[];
  /** Действующих договорённостей меньше двух — можно предлагать новые. */
  canPropose: boolean;
}

export interface PeriodReviewInput {
  /** Периоды по возрастанию; последний — идущий. */
  periods: readonly ReportPeriod[];
  txs: readonly AnalyticsTx[];
  categories: readonly ReviewCategory[];
  goals: readonly Goal[];
  goalEvents?: readonly { date: PlainDate; kind: string; amount: Money }[];
  creditCardIds?: readonly string[];
  deals: readonly PeriodDeal[];
  topicsOff: readonly PeriodTopicOff[];
}

/** Заметная трата: от 3 000 ₽ и выше обычного на 20 % и хотя бы на 1 500 ₽. */
const MIN_AMOUNT = 300_000 as Money;
const MIN_GAP = 150_000 as Money;
const MIN_LESS = 100_000 as Money;
const MAX_DEALS = 2;
const RELEASE_STREAK = 3;

const lengthOf = (p: ReportPeriod): number => diffDays(p.from, p.to ?? p.from) + 1;

/** Категория верхнего уровня: вложенные считаются вместе с родителем. */
const topOf = (cats: readonly ReviewCategory[], id: string | undefined): ReviewCategory | undefined => {
  let c = cats.find((x) => x.id === id);
  for (let i = 0; c?.parentId && i < 5; i++) c = cats.find((x) => x.id === c!.parentId) ?? c;
  return c;
};

/** Свободные траты по категориям верхнего уровня за отрезок: сумма и число покупок. */
function wantByCategory(input: PeriodReviewInput, from: PlainDate, to: PlainDate): Map<string, { sum: Money; count: number }> {
  const out = new Map<string, { sum: Money; count: number }>();
  for (const t of input.txs) {
    if (t.occurredDate < from || t.occurredDate > to) continue;
    const refund = t.type === 'income' && t.link?.kind === 'income' && t.link.incomeKind === 'refund';
    if (t.type !== 'expense' && !refund) continue;
    if (t.link && t.link.kind !== 'income') continue; // платежи и взносы — не «жизнь»
    const top = topOf(input.categories, t.categoryId);
    if (!top || top.kind === 'income') continue;
    const part = t.part ?? top.defaultPart;
    if (part !== 'want') continue;
    const cur = out.get(top.id) ?? { sum: ZERO, count: 0 };
    out.set(top.id, refund
      ? { sum: sub(cur.sum, t.amount), count: cur.count }
      : { sum: add(cur.sum, t.amount), count: cur.count + 1 });
  }
  return out;
}

/** Округление вверх до тысячи: «около 10 000 ₽», а не «9 900 ₽». */
const upToThousand = (m: Money): Money => (Math.ceil(m / 100_000) * 100_000) as Money;

/**
 * Разбор последнего закрытого периода; `null` — закрытого ещё нет.
 */
export function periodReview(input: PeriodReviewInput): PeriodReview | null {
  const closed = input.periods.filter((p) => p.to !== null);
  const period = closed[closed.length - 1];
  if (!period || !period.to) return null;
  const facts = periodFacts({
    txs: input.txs, from: period.from, to: period.to,
    ...(input.goalEvents ? { goalEvents: input.goalEvents } : {}),
    ...(input.creditCardIds ? { creditCardIds: input.creditCardIds } : {}),
  });
  const spent = add(facts.obligations, facts.need, facts.want);

  /*
    «Обычно» — серединный из прошлых полных периодов сопоставимой длины
    (±30 %), до шести: то же правило, что у «Обычно в месяц» (usual.ts). Неполный период (учёт начат посреди) и оборванный сменой дохода в
    сравнение не идут: их траты не за весь срок.
  */
  const len = lengthOf(period);
  const before = closed.slice(0, -1)
    .filter((p) => !p.partial && !p.endedBySwitch && Math.abs(lengthOf(p) - len) <= len * 0.3)
    .slice(-USUAL_MONTHS);
  const now = wantByCategory(input, period.from, period.to);
  const history = before.map((p) => wantByCategory(input, p.from, p.to!));
  const usualOf = (id: string): Money | null => (history.length === 0 ? null
    : Math.round(middle(history.map((h) => h.get(id)?.sum ?? 0))) as Money);
  const nameOf = (id: string) => input.categories.find((c) => c.id === id)?.name ?? 'Категория';

  // Цель, которую приблизит сэкономленное: первая по важности с ежемесячным взносом.
  const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const goal = [...input.goals]
    .filter((g) => g.status === 'active' && g.monthlyContribution > ZERO && g.currentAmount < g.targetAmount)
    .sort((a, b) => (order[a.priority] ?? 9) - (order[b.priority] ?? 9))[0];

  // Договорённости: проверяется каждый период после той, где договорились.
  const keyIndex = new Map(input.periods.map((p, i) => [p.key, i]));
  const at = keyIndex.get(period.key) ?? -1;
  const deals: DealResult[] = [];
  for (const d of input.deals) {
    if (d.droppedAt) continue;
    const from = keyIndex.get(d.periodKey);
    if (from === undefined || from >= at) continue;
    const spentNow = now.get(d.categoryId)?.sum ?? ZERO;
    let streak = 0;
    for (let i = at; i > from; i--) {
      const p = input.periods[i]!;
      if (!p.to) break;
      const s = wantByCategory(input, p.from, p.to).get(d.categoryId)?.sum ?? ZERO;
      if (s <= d.target) streak += 1; else break;
    }
    const agreed = input.periods[from]!;
    const was = agreed.to ? wantByCategory(input, agreed.from, agreed.to).get(d.categoryId)?.sum ?? ZERO : ZERO;
    deals.push({ deal: d, name: nameOf(d.categoryId), spent: spentNow, kept: spentNow <= d.target, less: sub(was, spentNow), streak });
  }
  const active = input.deals.filter((d) => !d.droppedAt
    && !deals.some((r) => r.deal === d && r.streak >= RELEASE_STREAK)).length;

  const off = new Set(input.topicsOff.map((t) => t.categoryId));
  const dealt = new Set(input.deals.filter((d) => !d.droppedAt).map((d) => d.categoryId));
  const observations: Observation[] = [];
  for (const [id, v] of now) {
    if (off.has(id) || dealt.has(id)) continue;
    const usual = usualOf(id);
    if (usual === null || v.sum < MIN_AMOUNT) continue;
    if (v.sum < usual * 1.2 || sub(v.sum, usual) < MIN_GAP) continue;
    const target = upToThousand(Math.round(usual * 1.1) as Money);
    const less = sub(v.sum, target);
    if (less < MIN_LESS) continue;
    const months = goal ? Math.round((less / goal.monthlyContribution) * 10) / 10 : 0;
    observations.push({
      categoryId: id, name: nameOf(id), amount: v.sum, count: v.count, usual, target, less,
      ...(goal && months >= 0.5 ? { goal: { id: goal.id, name: goal.name, months: Math.round(months) } } : {}),
    });
  }
  // Главная — та, что даёт больше денег на цели; затем ещё две.
  observations.sort((a, b) => b.less - a.less);

  return {
    period, income: facts.income, spent, saved: facts.goals,
    observations: observations.slice(0, 3), deals, canPropose: active < MAX_DEALS,
  };
}

/** Можно ли добавить договорённость: действующих меньше двух. */
export const dealsLeft = (deals: readonly PeriodDeal[]): number =>
  Math.max(0, MAX_DEALS - deals.filter((d) => !d.droppedAt).length);
